// @ts-nocheck

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { ApiResponse, ApiError } from '@/shared/application/types';
import { store } from '../store';
import { logout } from '../store/authSlice';
import { announceIdentity } from '../auth/crossTabIdentity';
import { X_API_KEY } from './runtimeEnv';

export interface HttpClientConfig {
  baseURL: string;
  timeout?: number;
  defaultHeaders?: Record<string, string>;
}

export class HttpClient {
  private client: AxiosInstance;
  private accessToken: string | null = null;

  constructor(config: HttpClientConfig) {
    this.client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': X_API_KEY,
        ...config.defaultHeaders,
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const language = store.getState().language.currentLanguage;
        config.headers['Accept-Language'] = language;
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => response,
      async (error: AxiosError) => {
        // A 401 from the session probe is its ANSWER — "nobody is signed in" — not a failure, so it
        // must not trigger the sign-out-and-redirect below. Without this exemption the probe would
        // throw every signed-out visitor at /login, including a candidate part-way through their own
        // I-9 on the public Section 1, remote-I9 and remediation screens.
        const isSessionProbe = (error.config?.url ?? '').includes('/auth/session');
        // The VTK-2009 Section 1 Started beacon is fire-and-forget from a candidate-facing screen; a
        // 401 there must not throw the candidate onto the staff login.
        const isSection1StartedBeacon = (error.config?.url ?? '').includes(
          '/i9Form/section1/started'
        );

        // Handle 401 Unauthorized - logout user
        if (error.response?.status === 401 && !isSessionProbe && !isSection1StartedBeacon) {
          try {
            // Call logout API endpoint (using axios directly to avoid interceptor loop)
            await axios.post(
              `${this.client.defaults.baseURL}/auth/logout`,
              {},
              { withCredentials: true }
            );
          } catch {
            // Ignore logout API errors - we're logging out anyway
          }
          // The cookie this took away is the whole browser's, so the other tabs are signed out
          // too — they just do not know it until their own next request fails. Tell them now,
          // rather than leaving an authenticated-looking interface up for a session that is gone.
          announceIdentity(null);
          store.dispatch(logout());
          window.location.href = '/login';
        }

        const apiError: ApiError = {
          message: error.response?.data?.errorMessage || error.message || 'An error occurred',
          status: error.response?.status || 500,
          code: error.response?.data?.errorCode || 'UNKNOWN_ERROR',
          redirectUrl: error.response?.data?.redirectUrl,
          i9Status: error.response?.data?.i9Status,
        };

        // A request that never reached the server (the browser was offline, e.g. a laptop waking
        // before its wifi returns) or that we cancelled is not a defect either; it filed most of
        // VTK-1860's "Network Error" alerts. A breadcrumb keeps it visible beside a real error.
        // A timeout has no response too, but it means the server was slow, so it stays reported.
        const neverAnswered =
          !error.response && (error.code === AxiosError.ERR_NETWORK || axios.isCancel(error));
        if (neverAnswered) {
          Sentry.addBreadcrumb({
            category: 'http',
            level: 'warning',
            message: `No response: ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
            data: { code: error.code },
          });
        }

        // A 401 is an expected answer (signed out, expired session, the session probe), not a
        // defect, and every page load would otherwise file one (VTK-1984).
        if (apiError.status !== 401 && !neverAnswered) {
          Sentry.captureException(error, {
            tags: {
              'http.status_code': apiError.status,
              'http.error_code': apiError.code,
            },
            extra: {
              // S5 (PII-leak hardening): do NOT send the raw response body — it can echo I-9 PII.
              // Status/code tags + the (non-PII) error message are enough for triage.
              url: error.config?.url,
              method: error.config?.method,
              errorMessage: apiError.message,
            },
          });
        }

        return Promise.reject(apiError);
      }
    );
  }

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  defaultConfig: AxiosRequestConfig = {
    withCredentials: true,
  };
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url, {
      ...config,
      ...this.defaultConfig,
    });
    return response.data;
  }

  async getPaginated<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<PaginatedResponse<T>> {
    const response = await this.client.get<PaginatedResponse<T>>(url, {
      ...config,
      ...this.defaultConfig,
    });
    return response.data;
  }
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, data, {
      ...config,
      ...this.defaultConfig,
    });
    return response.data;
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data, {
      ...config,
      ...this.defaultConfig,
    });
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url, {
      ...config,
      ...this.defaultConfig,
    });
    return response.data;
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, {
      ...config,
      ...this.defaultConfig,
    });
    return response.data;
  }
}
